import { useState } from "react";
import { useTranslation } from "react-i18next";
import TemplateTable, { ColumnDefCustom } from "@/shared/components/ui/table/table.tsx";
import { Card, CardContent, CardHeader, CardTitle } from "@/ui/components/ui/card.tsx";
import { useGetAlerts } from "@/alerts/api/queries/machine-alerts.queries.ts";
import { useFilterStore } from "@/shared/stores/useFilterStore.ts";
import { QUERY_KEYS } from "@/alerts/api/config.ts";
import { formatDate } from "@/shared/lib/i18n/formatters.ts";
import { alertStatusLabel } from "@/machines/utils/alert-status-label.ts";
import { Button } from "@/ui/components/ui/button.tsx";
import { useDateTimeRangeStore } from "@/shared/stores/useDateTimeRangeStore.ts";
import { AlertStatus, Alert } from "@/alerts/types/machine-alert.ts";
import { Badge } from "@/ui/components/ui/badge.tsx";
import { useModal } from "@/shared/lib/modal/modal-context.tsx";
import AlertsFilterBar, { AlertsFilterState } from "@/machines/components/table/alerts-filter-bar.tsx";

interface AlertsTableProps {
  device_mxid?: string;
  setSelectedTime?: (time: number, source: string) => void;
  shouldModalOpen: boolean;
}

const AlertsTable = ({ device_mxid, setSelectedTime, shouldModalOpen = false }: AlertsTableProps) => {
  const { t } = useTranslation("machines");
  const { getFilters, setFilters } = useFilterStore();
  const { value } = useDateTimeRangeStore();
  const { openModal } = useModal();
  const filterKey = `${QUERY_KEYS.ALERTS}/${device_mxid}`;
  const [alertFilters, setAlertFilters] = useState<AlertsFilterState>({
    ordering: "-timestamp",
  });

  const filters = getFilters(filterKey) as { page?: number; itemsPerPage?: number };

  const handleFilterChange = (nextFilters: AlertsFilterState) => {
    setAlertFilters(nextFilters);
    setFilters(filterKey, { page: 1 });
  };

  const handleClearFilters = () => {
    setAlertFilters({ ordering: "-timestamp" });
    setFilters(filterKey, { page: 1 });
  };

  const { data, status } = useGetAlerts({
    device_mxid,
    start_time: value.fromDate,
    end_time: value.toDate,
    page: filters?.page ?? 1,
    itemsPerPage: filters?.itemsPerPage ?? 10,
    ...alertFilters,
  });

  const alertsColumns: ColumnDefCustom<Alert>[] = [
    {
      header: t("alertsTable.columns.dateTime"),
      accessorKey: "timestamp",
      cell: ({ row }) => formatDate(new Date(row.original.timestamp), "P HH:mm:ss"),
    },
    {
      header: t("alertsTable.columns.machine"),
      accessorKey: "device_mxid",
    },
    {
      header: t("alertsTable.columns.eventType"),
      accessorKey: "type",
    },
    {
      header: t("alertsTable.columns.category"),
      accessorKey: "origin",
    },
    {
      header: t("alertsTable.columns.status"),
      accessorKey: "status",
      cell: ({ row }) => {
        const status = row.original.status as AlertStatus;

        const variantMap: Record<AlertStatus, "default" | "secondary" | "destructive" | "outline" | null | undefined> = {
          [AlertStatus.Pending]: "secondary",
          [AlertStatus.Resolved]: "default",
          [AlertStatus.Ignored]: "outline",
        };

        return (
          <Badge variant={variantMap[status]}>
            {alertStatusLabel(status, t)}
          </Badge>
        );
      },
    },
    {
      header: t("alertsTable.columns.reported"),
      accessorKey: "timestamp",
      id: "reported",
      cell: ({ row }) => formatDate(new Date(row.original.timestamp)),
    },
    {
      header: "",
      id: "viewShow",
      cell: ({ row }) => {
        const handleClick = () => {
          if (!row.original.id) return;
          if (shouldModalOpen) openModal("alert-detail", { alertId: row.original.id.toString() });

          // Only interact with timeline if setSelectedTime is provided
          if (setSelectedTime) {
            const timestamp = new Date(row.original.timestamp).getTime() + 100;
            setSelectedTime(timestamp, "external");
            // Scroll to the element with id="main-preview"
            const previewElement = document.getElementById("main-preview");
            if (previewElement) {
              previewElement.scrollIntoView({ behavior: "smooth", block: "start" });
            }
          }
        };
        return (
          <Button variant="outline" size="sm" onClick={handleClick}>
            {t("alertsTable.view")}
          </Button>
        );
      },
      enableSorting: false,
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("alertsTable.title")}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <AlertsFilterBar
          value={alertFilters}
          onChange={handleFilterChange}
          onClear={handleClearFilters}
        />
        <TemplateTable
          items={data?.data}
          columns={alertsColumns}
          pagination={data?.metadata}
          filterKey={filterKey}
          isLoading={status === "pending"}
        />
      </CardContent>
    </Card>
  );
};

export default AlertsTable;
